// client/src/components/plataforma/siteInstitucional/AccountTestimonialsPage.js
import React, { useState, useEffect } from 'react';
import { useTestimonials } from '../../../hooks/useTestimonials';
import PageHeader from '../../layout/PageHeader';
import { Loader2, MessageSquareQuote, CheckCircle, XCircle, Star, MessageSquare, Trash2, Search, MoreVertical } from 'lucide-react';
import ActionsDrawer from '../../common/ActionsDrawer';

const AccountTestimonialsPage = () => {
    const { testimonials, loading, error, fetchAccountTestimonials, updateAccountTestimonialStatus, deleteAccountTestimonial } = useTestimonials();

    const [filters, setFilters] = useState({ status: '', search: '' });
    const [selectedTestimonial, setSelectedTestimonial] = useState(null);

    useEffect(() => {
        fetchAccountTestimonials(filters);
    }, [filters]);

    const handleFilterChange = e => {
        setFilters({ ...filters, [e.target.name]: e.target.value });
    };

    const handleStatusChange = async (id, status) => {
        if (window.confirm(`Tem certeza que deseja ${status === 'approved' ? 'aprovar' : 'rejeitar'} este depoimento?`)) {
            await updateAccountTestimonialStatus(id, status);
            setSelectedTestimonial(null);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Tem certeza que deseja excluir este depoimento permanentemente?')) {
            await deleteAccountTestimonial(id);
            setSelectedTestimonial(null);
        }
    };

    const renderStatusBadge = (status) => {
        const styles = {
            pending: 'bg-yellow-100 text-yellow-800',
            approved: 'bg-green-100 text-green-800',
            rejected: 'bg-red-100 text-red-800',
        };
        return <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-800'}`}>{status}</span>;
    };
    
    return (
        <div className="page-management-container">
            <PageHeader
                title="Depoimentos da Conta"
                icon={<MessageSquareQuote className="icon-title" />}
                subtitle="Gerencie os depoimentos enviados pelos seus clientes."
            />
            
            <div className="content-panel mt-6">
                {/* Filtros */}
                <form className="flex flex-wrap items-center gap-4 mb-6">
                    <input 
                        type="text" 
                        name="search" 
                        value={filters.search} 
                        onChange={handleFilterChange} 
                        placeholder="Buscar por autor ou texto..." 
                        className="form-input flex-grow" 
                    />
                    <select 
                        name="status" 
                        value={filters.status} 
                        onChange={handleFilterChange} 
                        className="form-input w-full md:w-auto"
                    >
                        <option value="">Todos os Status</option>
                        <option value="pending">Pendentes</option>
                        <option value="approved">Aprovados</option>
                        <option value="rejected">Rejeitados</option>
                    </select>
                </form>

            </div>

            {/* ✅ ACHADO: table-th/table-tr/table-td não existem em lugar
                nenhum do SCSS (mesmo achado de TestimonialsManagementPage.js,
                a versão matriz/franquia desta mesma tela) — tabela inteira
                sem estilo dessas classes. Convertido pro padrão
                table-container/table-wrapper/styled-table. */}
            <div className="table-container">
                <div className="table-wrapper">
                    {loading ? (
                        <div className="table-loading-state">
                            <Loader2 size={24} className="animate-spin" />
                            <span>Carregando depoimentos...</span>
                        </div>
                    ) : error ? (
                        <div className="alert alert-danger">{error}</div>
                    ) : testimonials.length > 0 ? (
                        <table className="styled-table">
                            <thead>
                                <tr>
                                    <th style={{ textAlign: 'left' }}>Autor</th>
                                    <th style={{ textAlign: 'left' }}>Depoimento</th>
                                    <th>Avaliação</th>
                                    <th>Status</th>
                                    <th className="text-right">Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {testimonials.map(t => (
                                    <tr key={t._id}>
                                        <td style={{ textAlign: 'left' }} className="font-medium text-gray-900">
                                            {t.authorName} {t.authorRole && <span className="text-sm text-gray-500">({t.authorRole})</span>}
                                            {t.companyName && <p className="text-xs text-gray-400">{t.companyName}</p>}
                                        </td>
                                        <td style={{ textAlign: 'left' }} className="max-w-xs md:max-w-md">
                                            <p className="line-clamp-2 text-gray-700">{t.quote}</p>
                                        </td>
                                        <td>
                                            <div className="flex items-center justify-center">
                                                {[...Array(t.rating)].map((_, i) => <Star key={i} size={16} className="text-yellow-400 fill-current" />)}
                                                {[...Array(5 - t.rating)].map((_, i) => <Star key={i} size={16} className="text-gray-300" />)}
                                            </div>
                                        </td>
                                        <td>{renderStatusBadge(t.status)}</td>
                                        <td className="text-right">
                                            <button
                                                onClick={() => setSelectedTestimonial(t)}
                                                className="action-icon-button text-gray-600"
                                                title="Ações"
                                            >
                                                <MoreVertical size={18}/>
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        <div className="table-empty-state">
                            <MessageSquare size={48} />
                            <h3>Nenhum depoimento encontrado</h3>
                            <p>Nenhum depoimento encontrado para sua conta.</p>
                        </div>
                    )}
                </div>
            </div>

            <ActionsDrawer
                open={!!selectedTestimonial}
                onClose={() => setSelectedTestimonial(null)}
                title={selectedTestimonial?.authorName || 'Depoimento'}
                subtitle={selectedTestimonial?.companyName}
                actions={selectedTestimonial ? [
                    ...(selectedTestimonial.status !== 'approved' ? [{
                        key: 'aprovar',
                        label: 'Aprovar',
                        icon: <CheckCircle size={20} className="icon" />,
                        onClick: () => handleStatusChange(selectedTestimonial._id, 'approved'),
                        success: true,
                    }] : []),
                    ...(selectedTestimonial.status !== 'rejected' ? [{
                        key: 'rejeitar',
                        label: 'Rejeitar',
                        icon: <XCircle size={20} className="icon" />,
                        onClick: () => handleStatusChange(selectedTestimonial._id, 'rejected'),
                    }] : []),
                    {
                        key: 'excluir',
                        label: 'Excluir',
                        icon: <Trash2 size={20} className="icon" />,
                        onClick: () => handleDelete(selectedTestimonial._id),
                        destructive: true,
                    },
                ] : []}
            />
        </div>
    );
};

export default AccountTestimonialsPage;